import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Badge, Spinner, Alert, Form, InputGroup } from 'react-bootstrap';
import { FaSearch } from 'react-icons/fa';
import api from '../services/api';

const CourseList = () => {
    const navigate = useNavigate();
    const [courses, setCourses] = useState([]);
    const [filteredCourses, setFilteredCourses] = useState([]);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchCourses();
        fetchUser();
    }, []);

    const fetchUser = async () => {
        try {
            const response = await api.get('/users/me/');
            setUser(response.data);
        } catch (err) {
            console.error('Błąd pobierania użytkownika:', err);
        }
    };

    const fetchCourses = async () => {
        try {
            const response = await api.get('/courses/');
            setCourses(response.data);
            setFilteredCourses(response.data);
        } catch (err) {
            setError('Błąd pobierania kursów');
        } finally {
            setLoading(false);
        }
    };

    const isInstructor = user?.role === 'instructor';
    const isStudent = user?.role === 'student';

    // FUNKCJA WYSZUKIWANIA
    const handleSearch = (e) => {
        const term = e.target.value.toLowerCase();
        setSearchTerm(term);
        
        if (term === '') {
            setFilteredCourses(courses);
        } else {
            const filtered = courses.filter(course =>
                course.title.toLowerCase().includes(term) ||
                course.description?.toLowerCase().includes(term) ||
                course.tags?.some(tag => tag.toLowerCase().includes(term)) ||
                course.instructor_name?.toLowerCase().includes(term)
            );
            setFilteredCourses(filtered);
        }
    };

    const getDifficultyBadge = (difficulty) => {
        const variants = {
            beginner: 'success',
            intermediate: 'warning',
            advanced: 'danger'
        };
        const labels = {
            beginner: 'Początkujący',
            intermediate: 'Średniozaawansowany',
            advanced: 'Zaawansowany'
        };
        return <Badge bg={variants[difficulty] || 'secondary'}>{labels[difficulty] || difficulty}</Badge>;
    };

    const logout = () => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
    };

    if (loading) {
        return (
            <Container className="text-center mt-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Ładowanie kursów...</p>
            </Container>
        );
    }

    return (
        <Container className="mt-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1>Kursy</h1>
                <div className="d-flex gap-2">
                    {isInstructor && (
                        <Button 
                            variant="primary" 
                            onClick={() => navigate('/courses/create')}
                            style={{ backgroundColor: '#6366f1', borderColor: '#6366f1' }}
                        >
                            + Dodaj kurs
                        </Button>
                    )}

                </div>
            </div>

            {/* WYSZUKIWARKA */}
            <div className="mb-4">
                <InputGroup>
                    <InputGroup.Text>
                        <FaSearch />
                    </InputGroup.Text>
                    <Form.Control
                        type="text"
                        placeholder="Szukaj kursów po tytule, opisie, tagach lub instruktorze..."
                        value={searchTerm}
                        onChange={handleSearch}
                    />
                    {searchTerm && (
                        <Button 
                            variant="outline-secondary" 
                            onClick={() => {
                                setSearchTerm('');
                                setFilteredCourses(courses);
                            }}
                        >
                            ✕
                        </Button>
                    )}
                </InputGroup>
                {searchTerm && (
                    <div className="mt-2">
                        <small className="text-muted">
                            Znaleziono {filteredCourses.length} kursów
                        </small>
                    </div>
                )}
            </div>

            {/* Error */}
            {error && <Alert variant="danger">{error}</Alert>}

            {/* Courses grid */}
            {filteredCourses.length === 0 ? (
                <Alert variant="info">
                    {searchTerm ? 'Brak kursów spełniających kryteria wyszukiwania' : 'Brak dostępnych kursów'}
                </Alert>
            ) : (
                <Row>
                    {filteredCourses.map((course) => (
                        <Col md={6} lg={4} key={course.id} className="mb-4">
                            <Card className="h-100 shadow-sm">
                                <Card.Body>
                                    <Card.Title>{course.title}</Card.Title>
                                    <Card.Text className="text-muted">
                                        {course.description?.substring(0, 100)}...
                                    </Card.Text>
                                    <div className="mb-3">
                                        {getDifficultyBadge(course.difficulty)}
                                    </div>
                                    <div className="text-muted small mb-3">
                                        👨‍🏫 {course.instructor_name}
                                    </div>
                                    {course.tags?.length > 0 && (
                                        <div className="mb-3">
                                            {course.tags.slice(0, 3).map(tag => (
                                                <Badge key={tag} bg="secondary" className="me-1">
                                                    #{tag}
                                                </Badge>
                                            ))}
                                            {course.tags.length > 3 && (
                                                <Badge bg="secondary">+{course.tags.length - 3}</Badge>
                                            )}
                                        </div>
                                    )}
                                    {isStudent && course.is_published && (
                                        <Badge bg="info">📖 Otwarty</Badge>
                                    )}
                                </Card.Body>
                                <Card.Footer className="bg-white">
                                    <Button 
                                        variant="outline-primary" 
                                        className="w-100"
                                        onClick={() => navigate(`/courses/${course.id}`)}
                                    >
                                        Zobacz kurs →
                                    </Button>
                                </Card.Footer>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}
        </Container>
    );
};

export default CourseList;